/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";
import { subscribeToAuthChanges, getUserProfile } from "@/services/firebaseAuth.service";
import type { DemoUserProfile } from "@/interfaces/demoUser.interface";
import posthog, { identifyDemoUser } from "@/lib/posthog";

interface AuthContextValue {
  user: User | null;
  profile: DemoUserProfile | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<DemoUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const previousUidRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (nextUser) => {
      setUser(nextUser);
      if (nextUser) {
        const userProfile = await getUserProfile(nextUser.uid).catch(() => null);
        setProfile(userProfile);
        if (userProfile) identifyDemoUser(userProfile);
      } else {
        setProfile(null);
        // Only reset if a signed-in user actually logged out — an anonymous
        // visitor's very first resolution is also `nextUser === null` and
        // shouldn't wipe their pre-login distinct_id/session.
        if (previousUidRef.current) posthog.reset();
      }
      previousUidRef.current = nextUser?.uid ?? null;
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  return (
    <AuthContext.Provider value={{ user, profile, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
